import { AIClipProcessor } from '@src/services/AIClipProcessor';
import { LogManager } from '@src/utility/Logger';

import type { ClipFragment } from '@src/services/AIClipProducer';
import type { IClipProcessorDeps } from '@src/services/IClipProcessorDeps';
import type { Video } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

// Saved-segment → combine (save-for-later flow). Production logic that turns a
// saved-segment group's transcript ranges into a compilation reel, kept out of
// AIClipProducer.ts (which is a decomposition target) and co-located with the
// feature. Reuses the clip-production primitives (AIClipProcessor) via imports.

const logger = LogManager.getLogger('service:SavedSegmentCombine');

export type SavedSegmentRange = {
  startIndex: number;
  endIndex: number;
};

/**
 * Build a full ClipFragment from a transcript index range, deriving
 * startTime/duration/text the same way the AI clip flow does
 * (see the inline derivation in createClipResultMapper): scan to the
 * nearest element with a valid `ts` / `end_ts` (indices may land on
 * punctuation or removed/cut words whose `ts` is null — see "Deleted
 * Element Timestamps" in CLAUDE.md), apply the timeline buffers, and
 * build the visible text by skipping removed/cut elements.
 *
 * `src` is intentionally left empty — the combine flow's reel metadata is
 * sourced from the story's source reference (buildReelMetadata), not from
 * per-fragment `src`. Returns null when the range has no usable timestamps.
 */
export function deriveClipFragmentFromIndices(
  transcriptElements: TranscriptElement[],
  startIndex: number,
  endIndex: number,
  title: string,
  theme: string = 'social clip',
): ClipFragment | null {
  if (
    startIndex == null ||
    endIndex == null ||
    startIndex < 0 ||
    endIndex >= transcriptElements.length ||
    startIndex > endIndex
  ) {
    return null;
  }

  let startEl: TranscriptElement | undefined;
  for (let si = startIndex; si <= endIndex; si++) {
    const el = transcriptElements[si];
    if (el && el.ts != null) {
      startEl = el;
      break;
    }
  }

  let endEl: TranscriptElement | undefined;
  for (let ei = endIndex; ei >= startIndex; ei--) {
    const el = transcriptElements[ei];
    if (el && el.end_ts != null) {
      endEl = el;
      break;
    }
  }

  if (!startEl || !endEl || startEl.ts == null || endEl.end_ts == null) {
    return null;
  }

  const startTime = startEl.ts - (startEl.buffer_before_ts || 0);
  const endTime = endEl.end_ts + (endEl.buffer_after_ts || 0);

  // `startEl` (first valid `ts`) and `endEl` (last valid `end_ts`) are selected
  // independently, so a range with one-sided or drifted timestamps can yield a
  // window whose end precedes its start — a negative/zero-duration fragment.
  // Skip it (the caller logs + drops the range) rather than emit a broken clip.
  if (endTime <= startTime) {
    return null;
  }

  const text = transcriptElements
    .slice(startIndex, Math.min(endIndex + 1, transcriptElements.length))
    .filter(el => el.value && el.state !== 'removed' && el.state !== 'cut')
    .map(el => el.value)
    .join(' ');

  return {
    title,
    text,
    duration: endTime - startTime,
    startTime,
    src: '',
    theme,
    transcriptPosition: { startIndex, endIndex },
  };
}

/**
 * Turn one saved-segment group's ranges into ClipFragments for the combine
 * flow. Sorts by transcript order (startIndex) so the reel plays the ranges
 * in the order they appear in the source, and drops any range with no usable
 * timestamps (logging how many were skipped). `name` is the group's name,
 * used as each fragment's title.
 */
function buildSegmentFragments(
  ranges: SavedSegmentRange[],
  transcriptElements: TranscriptElement[],
  name: string,
): ClipFragment[] {
  const sorted = [...ranges].sort((a, b) => a.startIndex - b.startIndex);
  const fragments: ClipFragment[] = [];
  for (const range of sorted) {
    const fragment = deriveClipFragmentFromIndices(
      transcriptElements,
      range.startIndex,
      range.endIndex,
      name,
    );
    if (fragment) {
      fragments.push(fragment);
    } else {
      logger.warn('buildSegmentFragments: skipped range with no timestamps', {
        name,
        startIndex: range.startIndex,
        endIndex: range.endIndex,
      });
    }
  }
  return fragments;
}

/**
 * Combine one saved-segment group's ranges into a compilation reel from the
 * Transcript & Speakers tab, where there is no loaded currentVideo. Mirrors
 * saveClipFromTranscriptTab but uses the reel save (AIClipProcessor.saveAsReel)
 * so it stays on the full-render path the adapter already supports. Returns
 * the saved reel's id/title, or null if fewer than 2 ranges resolve.
 */
export async function combineSavedSegmentsFromTab(
  deps: IClipProcessorDeps,
  sourceVideo: Video,
  ranges: SavedSegmentRange[],
  name: string,
  transcriptElements: TranscriptElement[],
): Promise<{ id: string; title: string } | null> {
  if (!sourceVideo) {
    throw new Error('combineSavedSegmentsFromTab: sourceVideo is required');
  }

  const fragments = buildSegmentFragments(ranges, transcriptElements, name);
  if (fragments.length < 2) {
    logger.warn('combineSavedSegmentsFromTab: need at least 2 valid ranges', {
      requested: ranges.length,
      resolved: fragments.length,
    });
    return null;
  }

  const clipProcessor = new AIClipProcessor(
    fragments,
    deps,
    sourceVideo,
    'creator-studio',
    deps.sceneDetections ?? undefined,
  );
  const reel = await clipProcessor.saveAsReel();
  await deps.addMultipleVideosToStory([reel]);
  logger.info('combineSavedSegmentsFromTab: reel saved and added to story', {
    videoId: reel.id,
    storyId: deps.story?.id,
    rangeCount: fragments.length,
  });
  return reel.id ? { id: reel.id, title: reel.title ?? '' } : null;
}
